'use client';

import { useState } from 'react';

import { dataLayerPush, trackActivateReward } from '@libs/analytics';
import { activateOffer, getOffers as fetchMemberOffers } from '@libs/api-proxy-client';
import {
    ACTIVATE_OFFER_POST_LOGIN_ACTION,
    logSentryError,
    OPEN_OFFER_DETAILS_POST_LOGIN_ACTION,
    type PostLoginIntent,
    SentryAcpTreatsRewardsErrors as SentryErrors,
    useAlertBanner,
    useAuthSignInClick,
    useDecisions,
    useLoyaltyContext,
    useOptimizely,
    usePageLayout,
} from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import { ensureAuth0AccessToken, evaluateSession, isUserLoggedIn } from '@libs/core';
import { SnackbarUtils } from '@libs/shared-ui/utils';

import { applyMemberState } from '../../utils/apply-member-state';
import { getActivateRewardSelection } from './autoshipPointsPayoutTracking';
import { type OfferPageSection, useOfferDrawerStore } from './useOfferDrawerStore';

export function getCardSize(offer?: Offer, isFeatured?: boolean) {
    if (isFeatured) {
        return 'featured';
    }

    return offer?.cardSize.toLowerCase() === 'large' ? 'large' : 'small';
}

export function useOfferCardActions() {
    const { featuredOffers, getOffers, isActivating, offers } = useLoyaltyContext();
    const { drawerOpen, selectedOffer, offerPageSection, openOffer, closeOffer, markOfferActivated } =
        useOfferDrawerStore();
    const { isAuthenticated, user } = usePageLayout();
    const { handleSignInClick } = useAuthSignInClick();
    const isAsPointsPayoutCtaEnabled = useDecisions('as_pts_pyt_cta');

    const { setAlert } = useAlertBanner();
    const { context } = useOptimizely();

    const [activatingOffers, setActivatingOffers] = useState<Set<string>>(new Set());

    function handleCardClick(offer: Offer, pageSection?: OfferPageSection) {
        openOffer(offer, pageSection);
        context?.trackEvent('offer_details_modal_click');
        // Reset error so pre-existing error doesn't display inside offer drawer
        setAlert(undefined);
    }

    function handleCloseDrawer() {
        closeOffer();

        // Reset error so error from offer drawer doesn't display on offer grid
        setAlert(undefined);
    }

    async function handleActivateOffer(
        offer: Offer | undefined,
        gtmSection: OfferPageSection,
        actionAfterLogin: PostLoginIntent['action'] = OPEN_OFFER_DETAILS_POST_LOGIN_ACTION
    ) {
        if (!offer?.isActivatable || offer.isActivated) {
            return;
        }

        const shouldActivate = actionAfterLogin === ACTIVATE_OFFER_POST_LOGIN_ACTION;

        if (!isAuthenticated && !user) {
            const postLoginIntent: PostLoginIntent = {
                action: actionAfterLogin,
                params: { offerId: offer.offerId, gtmSection },
            };
            handleSignInClick({
                postLoginIntent,
                legacyDrawerProps: {
                    onSuccess: () => handleOfferSignInSuccess(offer, shouldActivate, gtmSection),
                    onRegisterClick: () => {
                        try {
                            sessionStorage.setItem('registration:postLoginIntent', JSON.stringify(postLoginIntent));
                        } catch {
                            return;
                        }
                    },
                },
            });
            return;
        }

        await activateAuthenticatedOffer(offer, gtmSection);
    }

    async function handleOfferSignInSuccess(
        offer: Offer | string,
        shouldActivate: boolean,
        gtmSection?: OfferPageSection
    ) {
        try {
            const response = await fetchMemberOffers();
            if (!response) {
                throw new Error('Failed to fetch offers after sign-in');
            }
            if (!isUserLoggedIn()) return;

            const offerId = typeof offer === 'string' ? offer : offer.offerId;
            const memberOffer = response.offers.find(candidate => candidate.offerId === offerId);
            if (!memberOffer) return;

            const displayOffer =
                typeof offer === 'string'
                    ? (offers?.find(candidate => candidate.offerId === offerId) ??
                      featuredOffers?.find(candidate => candidate.offerId === offerId) ??
                      memberOffer)
                    : offer;
            const offerWithMemberState = applyMemberState(displayOffer, memberOffer);
            openOffer(offerWithMemberState, gtmSection);

            if (shouldActivate) {
                await activateAuthenticatedOffer(offerWithMemberState, gtmSection);
            }
        } catch (error) {
            SnackbarUtils.showPredefined('SERVER_ERROR');
            logSentryError(error, SentryErrors.OfferCard.ActivateOfferFailed);
        }
    }

    async function activateAuthenticatedOffer(offer: Offer, gtmSection?: OfferPageSection) {
        if (!offer.isActivatable || offer.isActivated) return;

        const activateOfferIdFromUrl =
            typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('activateOfferId');
        setActivatingOffers(prevOffers => new Set(prevOffers).add(offer.offerId));

        try {
            const sessionStillValid = await evaluateSession({});
            if (!sessionStillValid) {
                return;
            }
            await ensureAuth0AccessToken();

            if (!isUserLoggedIn()) {
                return;
            }

            const response = await activateOffer(offer);

            if (response && 'success' in response && response?.success) {
                setAlert(undefined);
                context?.trackEvent('offer_activations');

                if (gtmSection) {
                    trackActivateReward({
                        pageSection: gtmSection,
                        selection: getActivateRewardSelection({
                            offer,
                            isAsPointsPayoutCtaEnabled,
                            activateOfferIdFromUrl,
                        }),
                    });
                }

                // Update selectedOffer immediately to reflect activated state
                markOfferActivated(offer.offerId);
                // Fetch offers again from API to get updated data after activation
                await getOffers();
            } else {
                throw new Error(`Failed to activate an offer, offerId: ${offer.offerId}`);
            }
        } catch (error) {
            SnackbarUtils.showPredefined('SERVER_ERROR');

            dataLayerPush({
                event: 'error',
                site_section: gtmSection || 'treats rewards',
                error_message: `uh oh! [${offer.shortDescription}] order didn't activate. please try again.`,
            });

            logSentryError(error, SentryErrors.OfferCard.ActivateOfferFailed);
        } finally {
            setActivatingOffers(prevOffers => {
                const updatedSet = new Set(prevOffers);
                updatedSet.delete(offer.offerId);
                return updatedSet;
            });
        }
    }

    function showSpinner(offer?: Offer) {
        return offer ? activatingOffers.has(offer.offerId) : false;
    }

    return {
        drawerOpen,
        handleActivateOffer,
        handleCardClick,
        handleCloseDrawer,
        handleOfferSignInSuccess,
        isActivating,
        offerPageSection,
        selectedOffer,
        showSpinner,
    };
}
